import type { SVGProps } from 'react';

/**
 * Minimal hand-rolled icon set so the app carries no icon dependency. Each icon
 * is a 24×24 stroked glyph inheriting currentColor. Add new glyphs to PATHS.
 */
export type IconName =
  | 'overview'
  | 'floor'
  | 'bookings'
  | 'customers'
  | 'food'
  | 'menu'
  | 'rates'
  | 'games'
  | 'payments'
  | 'expenses'
  | 'insights'
  | 'reports'
  | 'clubs'
  | 'team'
  | 'settings'
  | 'search'
  | 'bell'
  | 'plus'
  | 'pause'
  | 'play'
  | 'stop'
  | 'crown'
  | 'clock'
  | 'bulb'
  | 'chevronRight'
  | 'chevronDown'
  | 'grid'
  | 'list'
  | 'signout'
  | 'calendar'
  | 'rupee'
  | 'trend'
  | 'membership';

const PATHS: Record<IconName, string> = {
  overview: 'M4 13h7V4H4v9Zm9 7h7v-9h-7v9ZM4 20h7v-5H4v5Zm9-16v5h7V4h-7Z',
  floor: 'M3 6h18v12H3V6Zm0 4h18M8 10v8m8-8v8',
  bookings: 'M7 3v3m10-3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
  customers: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm13 8v-1a4 4 0 0 0-3-3.87M16 5a3 3 0 0 1 0 6',
  food: 'M4 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3M6 3v18M15 3c-1.5 1-2 3-2 5s.5 3 2 3v10',
  menu: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  rates: 'M20.6 13.4 12 22l-9-9V4a1 1 0 0 1 1-1h8l8.6 8.6a1 1 0 0 1 0 1.8ZM7.5 7.5h.01',
  games: 'M6 12h4m-2-2v4m6-1h.01M18 12h.01M7 18a5 5 0 0 1-5-5l1-5a3 3 0 0 1 3-2h10a3 3 0 0 1 3 2l1 5a5 5 0 0 1-5 5c-2 0-2.5-2-4-2h-3c-1.5 0-2 2-4 2Z',
  payments: 'M3 10h18M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z',
  expenses: 'M9 14l2 2 4-4M5 3h14a1 1 0 0 1 1 1v17l-3-2-3 2-3-2-3 2V4a1 1 0 0 1 1-1Z',
  insights: 'M3 3v18h18M7 15l4-5 3 3 5-7',
  reports: 'M14 3v5h5M8 13h8M8 17h5M6 3h9l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z',
  clubs: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01',
  team: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm11 10v-2a4 4 0 0 0-3-3.87',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 6 19.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H2a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 3.3 6l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H8a1.7 1.7 0 0 0 1-1.6V2a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V8a1.7 1.7 0 0 0 1.6 1H22a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
  search: 'M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  plus: 'M12 5v14M5 12h14',
  pause: 'M10 4H6v16h4V4Zm8 0h-4v16h4V4Z',
  play: 'M6 4l14 8-14 8V4Z',
  stop: 'M6 6h12v12H6z',
  crown: 'M3 7l4 4 5-7 5 7 4-4-2 12H5L3 7Z',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm0-16v6l4 2',
  bulb: 'M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2Z',
  chevronRight: 'M9 6l6 6-6 6',
  chevronDown: 'M6 9l6 6 6-6',
  grid: 'M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  signout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  calendar: 'M7 3v3m10-3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
  rupee: 'M6 3h12M6 8h12M15 3c0 5-4 5-9 5 0 0 7 0 9 8',
  trend: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  membership:
    'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm5.5 3.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM6 16c0-1.4 1.1-2.5 2.5-2.5S11 14.6 11 16M14 10h5M14 13.5h3',
};

export interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 20, ...rest }: IconProps) {
  const isFill = name === 'pause' || name === 'play' || name === 'stop';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={isFill ? 'currentColor' : 'none'}
      stroke={isFill ? 'none' : 'currentColor'}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
